import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Chip,
} from '@mui/material';
import EmployeeSearch from '../../common/EmployeeSearch';
import {
  DISCREPANCY_MISMATCH_REASON_OPTIONS,
  DISCREPANCY_MISMATCH_REASON_LABELS,
  DISCREPANCY_MISMATCH_REASON_COLORS,
} from '../../../constants/discrepancyMismatchReasons';
import {
  DISCREPANCY_COMPARABLE_MAPPING_STATUS_OPTIONS,
  DISCREPANCY_MAPPING_STATUS_LABELS,
  DISCREPANCY_MAPPING_STATUS_COLORS,
} from '../../../constants/discrepancyMappingStatuses';
import {
  DISCREPANCY_RADAR_ONLY_REASON_OPTIONS,
  DISCREPANCY_RADAR_ONLY_REASON_LABELS,
  DISCREPANCY_RADAR_ONLY_REASON_COLORS,
} from '../../../constants/discrepancyRadarOnly';
import { FilterBar } from './styles';
import { VIEW_OPTIONS } from './discrepancyViewFilters';
import { getChipSx } from './chipStyles';


// Mirrors the Project Status / Client Party Type style on the projects page:
// MenuItems hold a Chip (not a Checkbox + label), and selected items render as
// inline Chips inside the input.
const renderChips = (selected, labels, colors) => {
  if (!selected || selected.length === 0) {
    return <em>None</em>;
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((value) => (
        <Chip
          key={value}
          label={labels[value] || value}
          size="small"
          sx={getChipSx(colors[value])}
        />
      ))}
    </Box>
  );
};

const DiscrepanciesFilterBar = ({
  matchFilter,
  mismatchReason,
  mappingStatus,
  radarOnlyReason,
  employee,
  onChange,
}) => {
  const isRadarOnly = matchFilter === 'radarOnly';
  const handleMatch = (e) => onChange({ matchFilter: e.target.value });
  const handleReason = (e) => {
    const value = e.target.value;
    onChange({ mismatchReason: typeof value === 'string' ? value.split(',') : value });
  };
  const handleStatus = (e) => {
    const value = e.target.value;
    onChange({ mappingStatus: typeof value === 'string' ? value.split(',') : value });
  };
  const handleRadarOnlyReason = (e) => {
    const value = e.target.value;
    onChange({ radarOnlyReason: typeof value === 'string' ? value.split(',') : value });
  };
  const handleEmployee = (selected) => onChange({ employee: selected || null });

  return (
    <FilterBar>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="dmd-match-label">Show</InputLabel>
        <Select
          labelId="dmd-match-label"
          label="Show"
          value={matchFilter || 'mismatches'}
          onChange={handleMatch}
        >
          {VIEW_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {isRadarOnly && (
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="dmd-radar-only-reason-label">Radar only reason</InputLabel>
          <Select
            labelId="dmd-radar-only-reason-label"
            multiple
            value={radarOnlyReason || []}
            onChange={handleRadarOnlyReason}
            input={<OutlinedInput label="Radar only reason" />}
            renderValue={(sel) => renderChips(sel, DISCREPANCY_RADAR_ONLY_REASON_LABELS, DISCREPANCY_RADAR_ONLY_REASON_COLORS)}
          >
            {DISCREPANCY_RADAR_ONLY_REASON_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Chip
                  label={opt.label}
                  size="small"
                  sx={getChipSx(DISCREPANCY_RADAR_ONLY_REASON_COLORS[opt.value])}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!isRadarOnly && (
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="dmd-reason-label">Mismatch reason</InputLabel>
        <Select
          labelId="dmd-reason-label"
          multiple
          value={mismatchReason || []}
          onChange={handleReason}
          input={<OutlinedInput label="Mismatch reason" />}
          renderValue={(sel) => renderChips(sel, DISCREPANCY_MISMATCH_REASON_LABELS, DISCREPANCY_MISMATCH_REASON_COLORS)}
        >
          {DISCREPANCY_MISMATCH_REASON_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Chip
                label={opt.label}
                size="small"
                sx={getChipSx(DISCREPANCY_MISMATCH_REASON_COLORS[opt.value])}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      )}

      {!isRadarOnly && (
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="dmd-mapping-label">Mapping status</InputLabel>
        <Select
          labelId="dmd-mapping-label"
          multiple
          value={mappingStatus || []}
          onChange={handleStatus}
          input={<OutlinedInput label="Mapping status" />}
          renderValue={(sel) => renderChips(sel, DISCREPANCY_MAPPING_STATUS_LABELS, DISCREPANCY_MAPPING_STATUS_COLORS)}
        >
          {DISCREPANCY_COMPARABLE_MAPPING_STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Chip
                label={opt.label}
                size="small"
                sx={getChipSx(DISCREPANCY_MAPPING_STATUS_COLORS[opt.value])}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      )}

      <EmployeeSearch
        value={employee || null}
        onChange={handleEmployee}
        label="Individual"
        placeholder="Search individuals…"
        width="100%"
        height="40px"
        showSelectedAvatar
        sx={{ minWidth: 220, flex: 1 }}
      />
    </FilterBar>
  );
};

export default DiscrepanciesFilterBar;
